import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, InlineName, itemNames, MoveButtons, OpenButton, reorderPatches, TD, TH } from "@/components/admin/menu/shared";

/**
 * Master groups: the top of the menu, the split every item ends up in: Food /
 * Beverages / Extras. Item → category → group → master group. On the "group" bill
 * layout (Settings › Billing › Bill Content) the printed bill sections by these.
 *
 * The tab also says what is NOT divided yet: an item reaches a master group only
 * if its category has a group and that group has a master group.
 *
 * Each row opens onto its groups, their categories and items, with groups added or
 * taken out in place (floor report 2026-10-19: the name was editable from outside
 * but there was no way to see what was inside).
 */
export default function MenuMasterGroupsTab({ data, readOnly, onOpenTab }) {
  const { categories, groups, masters } = data;
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [openId, setOpenId] = useState(null);

  if (!masters || !groups || !categories) {
    return <p className="px-3 py-6 text-meta text-muted-foreground">Loading master groups…</p>;
  }

  const rows = [...masters].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const groupById = Object.fromEntries(groups.map(group => [group.id, group]));
  const itemsIn = category => (category.items || []).length;

  // Where each category lands: its master group id, or a reason it lands nowhere.
  const placement = category => {
    const group = category.group_id != null ? groupById[category.group_id] : null;
    if (!group) return { master: null, reason: "category" };
    if (group.group_category_id == null) return { master: null, reason: "group", group };
    return { master: group.group_category_id };
  };
  const countsFor = masterId => {
    const inGroups = groups.filter(group => group.group_category_id === masterId);
    const inCategories = categories.filter(category => placement(category).master === masterId);
    return {
      groups: inGroups.length,
      categories: inCategories.length,
      items: inCategories.reduce((total, category) => total + itemsIn(category), 0),
    };
  };

  const ungroupedCategories = categories.filter(c => c.is_active !== false && placement(c).reason === "category" && itemsIn(c) > 0);
  const masterlessGroups = groups.filter(g => g.is_active && g.group_category_id == null);
  const masterlessGroupItems = categories
    .filter(c => c.is_active !== false && placement(c).reason === "group")
    .reduce((total, c) => total + itemsIn(c), 0);
  const looseItems = ungroupedCategories.reduce((t, c) => t + itemsIn(c), 0) + masterlessGroupItems;

  const add = async event => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    const result = await data.createMaster({ name: name.trim(), sort_order: (rows.length + 1) * 10 });
    setBusy(false);
    if (!result.ok) return toast.error(`Could not add master group: ${result.error}`);
    toast.success(`Master group "${name.trim()}" added`);
    setName("");
  };

  const rename = async (master, next) => {
    const result = await data.updateMaster(master.id, { name: next });
    if (!result.ok) toast.error(`Could not rename "${master.name}": ${result.error}`);
    else toast.success(`Renamed to "${next}"`);
    return result;
  };

  const move = async (index, delta) => {
    setBusy(true);
    const result = await data.reorderMasters(reorderPatches(rows, index, delta));
    setBusy(false);
    if (!result.ok) toast.error(`Could not reorder: ${result.error}`);
  };

  const setGroupMaster = async (group, masterId) => {
    const result = await data.updateGroup(group.id, { group_category_id: masterId });
    if (!result.ok) toast.error(`Could not move "${group.name}": ${result.error}`);
    return result;
  };

  const setActive = async (master, active) => {
    const result = active
      ? await data.updateMaster(master.id, { is_active: true })
      : await data.deactivateMaster(master.id);
    if (!result.ok) return toast.error(`Could not ${active ? "reactivate" : "deactivate"} "${master.name}": ${result.error}`);
    toast.success(active ? `"${master.name}" reactivated` : `"${master.name}" deactivated`);
  };

  return (
    <div className="space-y-3" data-testid="menu-masters-tab">
      <p className="text-meta text-muted-foreground">
        Every item sits in a category, every category in a group, and every group under a master group.
        On the “group” bill layout the printed bill is divided by these.
      </p>

      {!readOnly && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
          <label className="flex flex-col gap-1 text-meta font-medium">
            New master group
            <Input className="h-8 w-56 text-body" value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Extras" data-testid="menu-master-new-name" />
          </label>
          <Button variant="action" type="submit" size="sm" disabled={busy || !name.trim()} data-testid="menu-master-add">
            <Plus className="h-4 w-4" />Add Master Group
          </Button>
        </form>
      )}

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className={TH}>Name</th>
              <th className={`${TH} text-right`}>Groups</th>
              <th className={`${TH} text-right`}>Categories</th>
              <th className={`${TH} text-right`}>Items</th>
              <th className={TH}>Order</th>
              <th className={TH}>Active</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((master, index) => {
              const counts = countsFor(master.id);
              return (
                <Fragment key={master.id}>
                <tr className={master.is_active ? "" : "bg-muted/30"} data-testid={`menu-master-row-${master.id}`}>
                  <td className={TD}>
                    <span className="flex items-center gap-1">
                      <OpenButton open={openId === master.id} onClick={() => setOpenId(openId === master.id ? null : master.id)} label={`What is in ${master.name}`} testId={`menu-master-open-${master.id}`} />
                      <InlineName value={master.name} disabled={readOnly} label={`Name of ${master.name}`} onCommit={next => rename(master, next)} testId={`menu-master-name-${master.id}`} />
                    </span>
                  </td>
                  <td className={`${TD} text-right tabular-nums text-muted-foreground`}>{counts.groups}</td>
                  <td className={`${TD} text-right tabular-nums text-muted-foreground`}>{counts.categories}</td>
                  <td className={`${TD} text-right tabular-nums`} data-testid={`menu-master-items-${master.id}`}>{counts.items}</td>
                  <td className={TD}>
                    <MoveButtons index={index} count={rows.length} onMove={move} disabled={readOnly || busy} testIdPrefix={`menu-master-move-${master.id}`} />
                  </td>
                  <td className={TD}>
                    <Switch
                      checked={master.is_active !== false}
                      disabled={readOnly}
                      aria-label={`${master.name} active`}
                      onCheckedChange={next => (next ? setActive(master, true) : setConfirm(master))}
                      data-testid={`menu-master-active-${master.id}`}
                    />
                  </td>
                </tr>
                {openId === master.id && (
                  <tr data-testid={`menu-master-contents-${master.id}`}>
                    <td colSpan={6} className="bg-muted/20 px-3 py-2">
                      <MasterContents master={master} groups={groups} categories={categories} readOnly={readOnly} onMove={setGroupMaster} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-meta text-muted-foreground">No master groups yet. Add Food, Beverages and Extras above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {looseItems > 0 && (
        <div className="rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-meta text-warning" role="status" data-testid="menu-master-unassigned">
          <p className="font-medium">{looseItems === 1 ? "1 item is" : `${looseItems} items are`} not under any master group yet.</p>
          {masterlessGroups.length > 0 && (
            <p className="mt-1">
              Groups with no master group: {masterlessGroups.map(group => group.name).join(", ")}.{" "}
              <button type="button" className="underline" onClick={() => onOpenTab?.("groups")}>Assign them in Groups</button>
            </p>
          )}
          {ungroupedCategories.length > 0 && (
            <p className="mt-1">
              Categories with no group: {ungroupedCategories.map(category => category.name).join(", ")}.{" "}
              <button type="button" className="underline" onClick={() => onOpenTab?.("categories")}>Assign them in Categories</button>
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? `Deactivate "${confirm.name}"?` : ""}
        body={confirm ? `Its ${countsFor(confirm.id).groups} groups keep pointing at it, but it is no longer offered when assigning a group. Switch it back on here at any time.` : ""}
        confirmLabel="Deactivate Master Group"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const target = confirm; setConfirm(null); setActive(target, false); }}
        testId="menu-master-deactivate-dialog"
      />
    </div>
  );
}

/** A master group's groups, each with its categories and items; groups added or taken out here. */
function MasterContents({ master, groups, categories, readOnly, onMove }) {
  const inside = groups.filter(group => group.group_category_id === master.id);
  const others = groups.filter(group => group.group_category_id !== master.id && group.is_active !== false);
  return (
    <div className="space-y-2">
      {inside.length === 0 && <p className="text-meta text-muted-foreground">No groups in {master.name} yet.</p>}
      {inside.map(group => {
        const cats = categories.filter(category => category.group_id === group.id);
        return (
          <div key={group.id} data-testid={`menu-master-${master.id}-group-${group.id}`}>
            <div className="flex items-center gap-2 text-body">
              <span className="flex-1 font-semibold">{group.name}</span>
              {!readOnly && (
                <button type="button" className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-meta text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => onMove(group, null)} data-testid={`menu-master-${master.id}-remove-${group.id}`}>
                  <X className="h-3.5 w-3.5" />Take out
                </button>
              )}
            </div>
            {cats.length === 0 && <p className="pl-3 text-meta text-muted-foreground">No categories.</p>}
            {cats.map(category => (
              <p key={category.id} className="pl-3 text-meta">
                <span className="font-medium">{category.name}</span>
                <span className="text-muted-foreground"> · {(category.items || []).length} items{(category.items || []).length ? `: ${itemNames(category)}` : ""}</span>
              </p>
            ))}
          </div>
        );
      })}
      {!readOnly && others.length > 0 && (
        <Select value="" onValueChange={value => { const group = others.find(g => String(g.id) === value); if (group) onMove(group, master.id); }}>
          <SelectTrigger className="h-8 w-64 text-body" aria-label={`Add a group to ${master.name}`} data-testid={`menu-master-${master.id}-add-group`}>
            <SelectValue placeholder="Add a group…" />
          </SelectTrigger>
          <SelectContent>
            {others.map(group => (
              <SelectItem key={group.id} value={String(group.id)}>
                {group.name}{group.group_category_id == null ? " (no master group)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
